/**
 * PNG export: the canvas renderer at a higher pixel ratio, not a second path.
 */

import type { MeasureText } from '../geometry/drawable';
import { unionRects, worldBounds } from '../geometry/transform';
import type { SlateElement } from '../scene/types';
import { renderScene } from './canvas';

/**
 * Safari caps canvas area near 16 million pixels and Chrome caps a single
 * dimension at 65535. Exceeding either yields a blank image with no error, so
 * the scale is clamped and the caller is told.
 */
export const MAX_EXPORT_PIXELS = 16_000_000;
export const MAX_EXPORT_DIMENSION = 16_384;

export interface PngOptions {
  readonly scale?: number;
  readonly padding?: number;
  readonly background?: string | null;
}

export interface PngResult {
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
  /** Set when the requested scale had to be reduced to stay within canvas limits. */
  readonly clampedFrom: number | null;
}

export async function toPngBlob(
  elements: readonly SlateElement[],
  measure: MeasureText,
  opts: PngOptions = {},
): Promise<PngResult> {
  const requested = opts.scale ?? 2;
  const padding = opts.padding ?? 24;
  const live = elements.filter((el) => !el.isDeleted);
  if (live.length === 0) throw new Error('There is nothing on this board to export.');

  const bounds = unionRects(live.map(worldBounds)) ?? { x: 0, y: 0, width: 100, height: 100 };
  const cssWidth = Math.max(1, Math.ceil(bounds.width + padding * 2));
  const cssHeight = Math.max(1, Math.ceil(bounds.height + padding * 2));

  let scale = requested;
  const byArea = Math.sqrt(MAX_EXPORT_PIXELS / (cssWidth * cssHeight));
  const byDimension = MAX_EXPORT_DIMENSION / Math.max(cssWidth, cssHeight);
  scale = Math.max(0.1, Math.min(scale, byArea, byDimension));
  const clampedFrom = scale < requested - 1e-9 ? requested : null;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(cssWidth * scale));
  canvas.height = Math.max(1, Math.floor(cssHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser did not provide a 2D canvas for the export.');

  renderScene(
    ctx,
    live,
    {
      width: cssWidth,
      height: cssHeight,
      pixelRatio: scale,
      zoom: 1,
      scrollX: bounds.x - padding,
      scrollY: bounds.y - padding,
      background: opts.background ?? null,
    },
    measure,
  );

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  // toBlob is allowed to hand back null.
  if (!blob) throw new Error('The browser could not encode the PNG. Try a smaller board.');
  return { blob, width: canvas.width, height: canvas.height, scale, clampedFrom };
}
